import { type Signal, signal } from '@angular/core';

import type { ClaritySession } from './clarity-session.model';
import type { ClaritySessionEndReason } from './clarity-session-end-reason.model';

/**
 * The Clarity session, as every consumer of the port sees it.
 *
 * An abstract class rather than an interface so it doubles as the DI token: guards, components and
 * services inject `ClaritySessionProvider` and never learn which identity provider is behind it. The
 * implementation lives in `@efficiently/client-data-access-clarity-session` and is bound to this
 * class by the composition root.
 *
 * **Reading only — signing in is not on this port.** Clarity authenticates on the platform session,
 * so the session is established by the platform's own sign-in; this reads, renews and ends it.
 *
 * State is exposed as signals, never as promises or observables: a session established or ended
 * elsewhere reaches the implementation asynchronously, and a signal is what lets a template and a
 * `computed` react to it without a subscription.
 */
export abstract class ClaritySessionProvider {
	/** The current session, or `null` when nobody is signed in. */
	public abstract readonly session: Signal<ClaritySession | null>;

	/** Whether a session is currently held. Derived from {@link session}. */
	public abstract readonly isAuthenticated: Signal<boolean>;

	/**
	 * Why the last session ended, when that is known — `null` otherwise, and again once a session is held.
	 * Kept after {@link session} clears, because what follows the end — the unavailable screen, or none at
	 * all while the platform signs the reader out — is decided by it.
	 *
	 * Concrete, always `null` here: only an identity provider that can tell a forced sign-out from an expired
	 * one overrides it, and every other implementation — test doubles included — needs nothing.
	 */
	public readonly endReason: Signal<ClaritySessionEndReason | null> = signal(null).asReadonly();

	/**
	 * The reader's verbose chat — Clarity's per-user debug mode, in which a domain chat renders every agent
	 * tool call inline rather than only the dispatches and the cards. Display-only: their backend stores the
	 * setting and never reads it.
	 *
	 * Read with the identity, because it arrives in the same profile answer; kept apart from {@link session}
	 * because it is a preference of the reader, not a part of who they are. `false` whenever {@link session}
	 * is `null`.
	 *
	 * Concrete, always `false` here — brain's own value with no profile (`profile?.verboseChatEnabled ?? false`):
	 * only an implementation that reads the reader's profile overrides it.
	 */
	public readonly verboseChatEnabled: Signal<boolean> = signal(false).asReadonly();

	/**
	 * Asks the backend who the caller is, and makes the answer current.
	 *
	 * This is the only way to learn whether a session exists: the cookie behind it is `HttpOnly`, so
	 * the page cannot inspect it and cannot know on its own whether it is signed in. An expired
	 * platform session is renewed once, by the platform, before the answer is taken as final.
	 *
	 * Resolves with {@link session} left `null` when nobody is signed in. Rejects only when the
	 * question could not be asked at all — the session is cleared first either way, so a caller that
	 * reads state rather than the promise may treat a rejection as "not signed in".
	 */
	public abstract restore(): Promise<void>;

	/**
	 * Lets the next {@link restore} ask afresh: forgets {@link endReason} and any renewal the platform refused.
	 *
	 * Only for the reader's explicit retry. A refused renewal is remembered so that one dead session does not
	 * turn every activation into another renewal; calling this on every activation would undo exactly that.
	 */
	public abstract startOver(): void;

	/**
	 * Ends the session and clears {@link session}.
	 *
	 * Clears it only once the identity provider has actually ended it. A rejection therefore means the
	 * reader is **still signed in** — the credential behind a Clarity session is a cookie this
	 * application cannot revoke on its own, so reporting a sign-out that did not happen would be a
	 * lie the next navigation undoes.
	 */
	public abstract signOut(): Promise<void>;
}
